import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';
import { getOrganizationId } from '../organization';
import { getAiAnalyticsUrl } from '../aiAnalyticsUrl';

export interface WhatsAppFlowRecord {
  id: string;
  business_account_id: string;
  user_id: string | null;
  name: string;
  status: 'draft' | 'published' | string;
  meta_flow_id?: string | null;
  flow_token?: string | null;
  definition: Record<string, any>;
  categories?: string[];
  preview_url?: string | null;
  published_version?: string | null;
  last_synced_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface SaveWhatsAppFlowPayload {
  id?: string;
  businessAccountId: string;
  name: string;
  definition: Record<string, any>;
  categories?: string[];
  cloneFlowId?: string;
}

export const whatsappFlowsApi = {
  async list(businessAccountId: string, includeRemote = false) {
    return apiClient.get<{ data: WhatsAppFlowRecord[]; remote?: any[] }>(
      API_ENDPOINTS.WHATSAPP_FLOWS,
      {
        params: {
          businessAccountId,
          includeRemote,
        },
      }
    );
  },

  async get(id: string, businessAccountId: string) {
    return apiClient.get<WhatsAppFlowRecord>(
      `${API_ENDPOINTS.WHATSAPP_FLOWS}/${id}`,
      {
        params: {
          businessAccountId,
        },
      }
    );
  },

  async save(payload: SaveWhatsAppFlowPayload) {
    return apiClient.post<WhatsAppFlowRecord>(
      API_ENDPOINTS.WHATSAPP_FLOWS,
      {
        id: payload.id,
        businessAccountId: payload.businessAccountId,
        name: payload.name,
        definition: payload.definition,
        categories: payload.categories,
        cloneFlowId: payload.cloneFlowId,
      }
    );
  },

  async requestPreview(id: string, businessAccountId: string) {
    return apiClient.post<{ previewUrl?: string | null; preview_url?: string | null }>(
      `${API_ENDPOINTS.WHATSAPP_FLOWS}/${id}/preview`,
      {
        businessAccountId,
      }
    );
  },

  async publish(id: string, businessAccountId: string) {
    return apiClient.post<{ flowToken?: string | null }>(
      `${API_ENDPOINTS.WHATSAPP_FLOWS}/${id}/publish`,
      {
        businessAccountId,
      }
    );
  },

  async publishWithWebhook(id: string, businessAccountId: string) {
    return apiClient.post<{ flowToken?: string | null }>(
      `${API_ENDPOINTS.WHATSAPP_FLOWS}/${id}/publish-with-webhook`,
      {
        businessAccountId,
      }
    );
  },

  async remove(id: string, businessAccountId: string, options?: { deprecate?: boolean }) {
    const query = new URLSearchParams({
      businessAccountId,
    });
    if (options?.deprecate !== undefined) {
      query.set("deprecate", String(options.deprecate));
    }

    return apiClient.delete<{ id: string }>(
      `${API_ENDPOINTS.WHATSAPP_FLOWS}/${id}?${query.toString()}`
    );
  },

  async sync(businessAccountId: string) {
    return apiClient.post<{
      success: boolean;
      message: string;
      syncedCount: number;
      importedCount: number;
      updatedCount: number;
      totalRemote: number;
    }>(`${API_ENDPOINTS.WHATSAPP_FLOWS}/sync`, {
      businessAccountId,
    });
  },

  /**
   * Generate WhatsApp flow via AI service (form-builder/generate endpoint)
   */
  async generateAIFlow(data: {
    query: string;
    form_name?: string;
    primary_action_label?: string;
    include_marketing_opt_in?: boolean;
    force_minimal_tokens?: boolean;
  }) {
    const formData = new FormData();
    formData.append('query', data.query);
    if (data.form_name) {
      formData.append('form_name', data.form_name);
    }
    if (data.primary_action_label) {
      formData.append('primary_action_label', data.primary_action_label);
    }
    if (data.include_marketing_opt_in !== undefined) {
      formData.append('include_marketing_opt_in', String(data.include_marketing_opt_in));
    }
    if (data.force_minimal_tokens !== undefined) {
      formData.append('force_minimal_tokens', String(data.force_minimal_tokens));
    }
    const organizationId = getOrganizationId();
    if (organizationId) {
      formData.append('organization_id', organizationId);
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

    const response = await fetch(getAiAnalyticsUrl('/form-builder/generate'), {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      // Try to parse error response
      let errorData: any = {};
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json().catch(() => ({}));
      } else {
        const text = await response.text().catch(() => '');
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { detail: text || `HTTP ${response.status}` };
        }
      }

      const error = new Error(errorData.detail || errorData.error || errorData.message || `HTTP ${response.status}`);
      (error as any).response = response;
      (error as any).errorData = errorData;
      throw error;
    }

    return response.json();
  },
};

export default whatsappFlowsApi;
